import { ReactElement } from 'react';
import {
  AwardIcon,
  CheckMark,
  ClockIcon,
  DeleteIcon,
  DownloadIcon,
  FilledPlusIcon,
  PreviousIcon,
  ResendIcon,
} from '@root/src/assets/images';
import Button from '@root/src/app/designSystem/buttons';
import Lang from '@lang/en';
import { HandlersI } from './actions';
import { healthCheckDownloadCertificateUrl } from '@app/definition/API';

const generateUrlForDownloadCertificate = (): string => {
  const subPath = window.location.pathname.match(/\/(\d+)$/);
  const orderId = subPath && subPath.length ? subPath[1] : '';
  return healthCheckDownloadCertificateUrl.replace('%id%', orderId);
};

export type ButtonsI = {
  addResultsPrimaryButton: ReactElement;
  addResultsSecondaryButton: ReactElement;
  startTimerButton: ReactElement;
  deleteButton: ReactElement;
  markAsTestedButton: ReactElement;
  confirmButton: ReactElement;
  resendButton: ReactElement;
  previousStageButton: ReactElement;
  generateCertificateButton: ReactElement;
  downloadCertificateButton: ReactElement;
};

const getActionButtons = (handlers: HandlersI): ButtonsI => {
  const addResultsButtonProps = {
    'data-testid': 'healthcheck-order-add-results-button',
    label: Lang.healthCheckAddResultsText,
    noFill: true,
    icon: <FilledPlusIcon />,
    onClick: handlers.handleAddResultsOpen,
  };

  const addResultsPrimaryButton = (
    <Button {...addResultsButtonProps} type="primary" />
  );

  const addResultsSecondaryButton = (
    <Button {...addResultsButtonProps} type="secondary" />
  );

  const deleteButton = (
    <Button
      data-testid="healthcheck-order-delete-button"
      label={Lang.deleteText}
      type="danger"
      icon={<DeleteIcon />}
      onClick={handlers.handleDeleteOrderOpen}
    />
  );

  const startTimerButton = (
    <Button
      data-testid="healthcheck-order-start-timer-button"
      label={Lang.healthCheckStartTimerText}
      type="primary"
      icon={<ClockIcon />}
      onClick={handlers.handleTestPerformed}
    />
  );

  const markAsTestedButton = (
    <Button
      data-testid="healthcheck-order-mark-as-tested-button"
      label={Lang.healthCheckMarkAsTestedOrderText}
      type="primary"
      icon={<CheckMark />}
      onClick={handlers.handleTestPerformed}
    />
  );

  const confirmButton = (
    <Button
      data-testid="healthcheck-order-confirm-order-button"
      label={Lang.healthCheckConfirmOrderText}
      type="primary"
      noFill={true}
      icon={<CheckMark />}
      onClick={handlers.handleOrderConfirmed}
    />
  );

  const resendButton = (
    <Button
      data-testid="healthcheck-order-resend-certificate-button"
      label={Lang.healthCheckResendCertificateText}
      type="secondary"
      noFill={true}
      icon={<ResendIcon />}
      onClick={handlers.handleResendCertificate}
    />
  );

  const downloadCertificateButton = (
    <Button
      data-testid="healthcheck-order-download-certificate-button"
      label={Lang.healthCheckDownloadCertificateText}
      type="secondary"
      noFill={true}
      icon={<DownloadIcon />}
      as="a"
      href={generateUrlForDownloadCertificate()}
    />
  );

  const generateCertificateButton = (
    <Button
      data-testid="healthcheck-order-generate-certificate-button"
      label={Lang.healthCheckGenerateCertificateText}
      type="primary"
      icon={<AwardIcon />}
      onClick={handlers.handleGenerateCertificate}
    />
  );

  const previousStageButton = (
    <Button
      data-testid="healthcheck-order-add-results-button"
      type="secondary"
      icon={<PreviousIcon />}
      label={Lang.healthCheckPreviousStageText}
      onClick={handlers.handlePreviousStageOrderOpen}
    />
  );

  return {
    addResultsPrimaryButton,
    addResultsSecondaryButton,
    deleteButton,
    startTimerButton,
    markAsTestedButton,
    confirmButton,
    resendButton,
    previousStageButton,
    generateCertificateButton,
    downloadCertificateButton,
  };
};

export default getActionButtons;
